import { Inject, Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable,  BehaviorSubject, of } from 'rxjs';
import { map, catchError, tap, shareReplay } from 'rxjs/operators';
import { ICredentialResultResource, ILoggedUser, ILoginResource, IRefreshResource } from '../../models/icredential.interface';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MessageToastService } from '../common/message-toast.service';
import { CLAIM_TYPES } from 'src/app/shared/data/claims.data';
import { EventBusService } from '../common/event.service';


export class JWT {
  public static readonly ACCESS_TOKEN = 'accessToken';
  public static readonly REFERSH_TOKEN = 'refreshToken';
}

@UntilDestroy()
@Injectable({
  providedIn: 'root'
})
export class AuthService {

  //----
  private _authToken: string;
  private _refreshToken: string;
  _authState: BehaviorSubject<boolean> = new BehaviorSubject(false);

  private _isUser: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private _isAdmin: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private _isRoot: BehaviorSubject<boolean> = new BehaviorSubject(false);

  private _loggedUser: ILoggedUser;
  private _interruptedUrl: string = '';
  private _jwt = new JwtHelperService();

  private _initialData: string[] = [
    'JWT_TOKEN', 'interruptedUrl', 'REFRESH_TOKEN'
  ];
  private _url = 'auth';
  private _api = environment.apiUrl;

  constructor(
    @Inject('LOCALSTORAGE') private localStorage: Storage,
    private _http: HttpClient,
    private _msg: MessageToastService,
    private _router: Router,
    private _eventBus: EventBusService
  ) {
    this.resetAuthState();
    this.readAuthData();
  }

  private resetAuthState() {
    this._authState = new BehaviorSubject(!1);
    this._isUser = new BehaviorSubject(!1);
    this._isAdmin = new BehaviorSubject(!1);
    this._isRoot = new BehaviorSubject(!1);
  }

  checkExpiration(token: string) {
    if (token || '' === '') return;

    if (this._jwt.isTokenExpired(token)) {
      console.log('token expired');
      this._http.post<ICredentialResultResource>(`${this._api}${this._url}/refresh`, {
        email: this._jwt.decodeToken(token).email,
        refreshToken: this._refreshToken,
      } as IRefreshResource).
        pipe(
          untilDestroyed(this),
          tap(token => {
            console.log('resfresh');
            this.setToken(token);
          }),
          catchError(err => {
            console.log('logout');
            this.logOut();
            return of(null);
          })
        ).subscribe();
    }
  }

  private readAuthData() {
    this._initialData.forEach((value) => {
      this[value] = localStorage.getItem(value);
    });
    if (this['JWT_TOKEN']) {
      this.token = this['JWT_TOKEN'];
      this.decodeUserFromToken(this['JWT_TOKEN']);
      this._refreshToken = this['REFRESH_TOKEN'];
    }

  }
  private clearAuthData() {
    this._initialData.forEach((value) => {
      localStorage.removeItem(value);
    });
  }
  private decodeUserFromToken(token: string) {
    const decodedToken = this._jwt.decodeToken(token);
    this._loggedUser = {
      name: decodedToken.unique_name,
      email: decodedToken.email,
      role: decodedToken.role,
      nameIdentifier: decodedToken.nameid
    }
    // console.log(decodedToken);
    // console.log(this._loggedUser);

    if (this._loggedUser.role === 'user') {
      this._isUser.next(true);
      this._isAdmin.next(false);
      this._isRoot.next(false);
    }
    if (this._loggedUser.role === 'admin') {
      this._isUser.next(false);
      this._isAdmin.next(true);
      this._isRoot.next(false);
    }
    if (this._loggedUser.role === 'root') {
      this._isUser.next(false);
      this._isAdmin.next(true);
      this._isRoot.next(true);
    }
  }
  private setToken(token: ICredentialResultResource) {
    localStorage.setItem('JWT_TOKEN', token.accessToken);
    localStorage.setItem('REFRESH_TOKEN', token.refreshToken);

    this.readAuthData();
  }

  get rtoken(): string {
    return this._refreshToken || '';
  }
  get token(): string {
    return this._authToken ? this._authToken : '';
  }
  set token(token: string) {
    this._authToken = token;
    this._authState.next(!!token);
    if (token === '') {
      this._authState.next(false);
      this._isUser.next(false);
      this._isAdmin.next(false);
      this._isRoot.next(false);
      this._loggedUser = null;
    }
  }

  get currentUser(): ILoggedUser {
    return this._authState.value ? this._loggedUser : null;
  }

  get isExpired() : boolean {
    const token = this.token;
    return this._jwt.isTokenExpired(token);
  }

  get interruptedUrl(): string {
    return this._interruptedUrl;
  }
  set interruptedUrl(url: string) {
    this._interruptedUrl = url ?? '';
    console.log('interrupted url:', this._interruptedUrl);
    if (!url) {
      localStorage.removeItem('interruptedUrl');
    } else {
      localStorage.setItem('interruptedUrl', url);
    }
  }

  isRootUser(): boolean {
    return this._isRoot.value;
  }
  isAdminUser(): boolean {
    if (this._isAdmin.value) return this._isAdmin.value;
    else if (this._isRoot.value) return this._isRoot.value ;
    else return false;

  }
  isAuthenticated(): boolean {
    return this._authState.value;
  }
  logIn(credentials: ILoginResource) : Observable<boolean | ICredentialResultResource> {
    return this._http.post<ICredentialResultResource>(`${this._api}${this._url}/login`, credentials)
      .pipe(
        untilDestroyed(this),
        tap((response => {
          if (!response) return of(null);

          this.setToken(response);
          this._router.navigate([this.interruptedUrl && this.interruptedUrl.length ? this.interruptedUrl : '/'])
            .then(() => {
              this.interruptedUrl = '';
              this._msg.success("Вход выполнен");
              this._eventBus.emit('LOGIN');
            });
          return of(true);
        })),
        catchError((error) => {
          // console.log('auth catch err');
          this._msg.errorEx(error.error);
          return of(false);
        }),
      );
  }

  logOut() {
    this.token = '';
    this.clearAuthData();
    console.log('interrupted url: ', this.interruptedUrl);
    this._router.navigate([this.interruptedUrl && this.interruptedUrl.length ? this.interruptedUrl : '/'])
    .then(() => {
      this.interruptedUrl = '';
      this._msg.success("Выход");
      this._eventBus.emit('LOGOUT');
    });
  }
  check() {
    return this._http.get<Date>(`${this._api}${this._url}/check`).pipe(untilDestroyed(this)).subscribe();
  }
  refreshToken() {
    const form: IRefreshResource = {
      email: this._loggedUser ? this._loggedUser.email : '',
      refreshToken: this['REFRESH_TOKEN'],
    };
    return this._http.post<ICredentialResultResource>(`${this._api}${this._url}/refresh`, form)
      .pipe(
        untilDestroyed(this),
        map((data) => {
          // console.log('refresh token:', data)
          this.setToken(data);
          return this.token;
        })
      );
  }
}





